/* Hallmark · component: auth-register · genre: modern-minimal · theme: project system
 * states: default · hover · focus · active · disabled · loading · error · success(→ /verify-email)
 * pre-emit critique: P4 H5 E4 S5 R5 V4
 */

/**
 * RegisterPage — pdf-api's own sign-up screen.
 *
 * Wired through `config.auth.components.register`.
 *
 * ⚠ This screen captures the legally required consents. It mirrors the kernel
 * RegisterForm's contract exactly and deliberately:
 *   - terms AND privacy are both mandatory (zod `.refine(val === true)`),
 *   - the accepted ones become `consentTypes: ['terms', 'privacy']`, which
 *     `useSignUp` forwards to the backend as the consent record,
 *   - the social buttons stay disabled until the cookie banner is answered
 *     (`hasConsented`) — they set third-party cookies,
 *   - an `?invitation=` token routes the new user to /invite/accept afterwards.
 *
 * If the required documents ever change, this file and the kernel's
 * RegisterForm must change together. That duplication is the price of owning
 * the screen; the alternative was a sign-up that looks nothing like our login.
 *
 * Redesign vs. the kernel default: left-aligned instead of the centred card,
 * and the consent checkboxes are grouped under a hairline rather than floating
 * between the fields and the button — they are a distinct decision, not a
 * form field.
 */

import { zodResolver } from '@hookform/resolvers/zod'
import {
  Divider,
  GitHubButton,
  GoogleButton,
  MicrosoftButton,
  NAME_MIN_LAENGE,
  PASSWORT_MIN_LAENGE,
  useCookieConsent,
  useSaaSConfig,
  useSignUp,
  useSocialSignIn,
} from '@xhubio-saas/core-frontend/kernel'
import { Button, cn, Input, Label } from '@xhubio-saas/core-frontend/ui-components'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { AuthAlert, AuthHeading, CONTROL_TRANSITION, FIELD_RING } from './authUi'

type TranslateFn = (key: string, defaultValue?: string, options?: Record<string, unknown>) => string

/**
 * Mirrors core's createRegisterSchema — keep the two in step.
 *
 * The kernel sentences for `nameMin`/`passwordMin` carry `{{min}}`; without `{ min }` i18next shows the placeholder
 * verbatim. The numbers come from the kernel constants, so `.min()` and the sentence cannot drift apart
 * (plan CORE 2026-10-05/02 S4).
 */
const createRegisterSchema = (t: TranslateFn) =>
  z
    .object({
      name: z.string().min(
        NAME_MIN_LAENGE,
        t('auth.validation.nameMin', 'Name must be at least 2 characters', {
          min: NAME_MIN_LAENGE,
        })
      ),
      email: z.string().email(t('auth.validation.emailInvalid', 'Invalid email address')),
      password: z.string().min(
        PASSWORT_MIN_LAENGE,
        t('auth.validation.passwordMin', 'Password must be at least 8 characters', {
          min: PASSWORT_MIN_LAENGE,
        })
      ),
      confirmPassword: z.string(),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: t(
          'auth.validation.acceptTermsRequired',
          'You must accept the terms and conditions'
        ),
      }),
      acceptPrivacy: z.boolean().refine((val) => val === true, {
        message: t('auth.validation.acceptPrivacyRequired', 'You must accept the privacy policy'),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.validation.passwordsNoMatch', 'Passwords do not match'),
      path: ['confirmPassword'],
    })

type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>

export function RegisterPage() {
  const { t } = useTranslation()
  const { signUp } = useSignUp()
  const { signInWithGoogle, signInWithMicrosoft, signInWithGitHub } = useSocialSignIn()
  const { hasConsented } = useCookieConsent()
  const config = useSaaSConfig()
  const [searchParams] = useSearchParams()

  const [showPassword, setShowPassword] = useState(false)
  const [socialPending, setSocialPending] = useState<'google' | 'microsoft' | 'github' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isGoogleEnabled = config.auth?.providers?.includes('google') ?? false
  const isMicrosoftEnabled = config.auth?.providers?.includes('microsoft') ?? false
  const isGitHubEnabled = config.auth?.providers?.includes('github') ?? false
  const hasSocialProviders = isGoogleEnabled || isMicrosoftEnabled || isGitHubEnabled

  // Same shape as the kernel's RegisterForm: adapt i18next's TFunction to the
  // narrow (key, defaultValue, options) signature the schema wants, and memoize
  // so the resolver isn't rebuilt on every keystroke.
  const schema = useMemo(
    () =>
      createRegisterSchema((key, defaultValue, options) =>
        t(key, { ...options, defaultValue: defaultValue ?? key })
      ),
    [t]
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(schema),
    defaultValues: { acceptTerms: false, acceptPrivacy: false },
  })

  const onSubmit = async (data: RegisterFormData) => {
    setError(null)

    const consentTypes: string[] = []
    if (data.acceptTerms) consentTypes.push('terms')
    if (data.acceptPrivacy) consentTypes.push('privacy')

    const invitationToken = searchParams.get('invitation')
    const inviteAcceptPath = invitationToken ? `/invite/accept?token=${invitationToken}` : undefined

    const result = await signUp(data.email, data.password, data.name, {
      consentTypes,
      redirectTo: inviteAcceptPath,
      callbackURL: inviteAcceptPath ? `${window.location.origin}${inviteAcceptPath}` : undefined,
    })
    if (!result.success) setError(result.error || t('auth.registerFailed', 'Registration failed'))
  }

  const runSocial = async (
    provider: 'google' | 'microsoft' | 'github',
    fn: () => Promise<unknown>,
    failedKey: string,
    fallback: string
  ) => {
    setSocialPending(provider)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(t(failedKey, fallback))
      console.error(`[RegisterPage] ${provider} sign in error:`, err)
    } finally {
      setSocialPending(null)
    }
  }

  const busy = isSubmitting || socialPending !== null

  return (
    <div className="flex w-full flex-col gap-8">
      <AuthHeading
        title={t('auth.signUp', 'Sign up')}
        description={t('auth.signUpDescription', 'Create an account to get started')}
      />

      {error && <AuthAlert tone="error">{error}</AuthAlert>}

      {hasSocialProviders && (
        <div className="flex flex-col gap-3">
          {isGoogleEnabled && (
            <GoogleButton
              onClick={() =>
                runSocial('google', signInWithGoogle, 'auth.googleFailed', 'Google sign in failed')
              }
              isLoading={socialPending === 'google'}
              // Third-party cookies — blocked until the banner is answered.
              disabled={busy || !hasConsented}
              t={t}
            />
          )}
          {isMicrosoftEnabled && (
            <MicrosoftButton
              onClick={() =>
                runSocial(
                  'microsoft',
                  signInWithMicrosoft,
                  'auth.microsoftFailed',
                  'Microsoft sign in failed'
                )
              }
              isLoading={socialPending === 'microsoft'}
              disabled={busy || !hasConsented}
              t={t}
            />
          )}
          {isGitHubEnabled && (
            <GitHubButton
              onClick={() =>
                runSocial('github', signInWithGitHub, 'auth.githubFailed', 'GitHub sign in failed')
              }
              isLoading={socialPending === 'github'}
              disabled={busy || !hasConsented}
              t={t}
            />
          )}
          {/* Always pass a default: the kernel calls this key without one and it
              exists in no locale file, so it renders as the raw key path. */}
          {!hasConsented && (
            <p className="text-xs text-muted-foreground">
              {t(
                'auth.consentRequired',
                'Please answer the cookie banner first — signing in with Google or GitHub sets third-party cookies.'
              )}
            </p>
          )}
          <Divider t={t} />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
        <div className="flex flex-col gap-2">
          <Label htmlFor="name">{t('auth.name', 'Name')}</Label>
          <Input
            id="name"
            autoComplete="name"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'name-error' : undefined}
            className={cn(FIELD_RING, errors.name && 'border-destructive')}
            {...register('name')}
          />
          {errors.name && (
            <p id="name-error" className="text-xs text-destructive">
              {errors.name.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'register-email-error' : undefined}
            className={cn(FIELD_RING, errors.email && 'border-destructive')}
            {...register('email')}
          />
          {errors.email && (
            <p id="register-email-error" className="text-xs text-destructive">
              {errors.email.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="register-password">{t('auth.password', 'Password')}</Label>
          <div className="relative">
            <Input
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'register-password-error' : undefined}
              className={cn('pr-10', FIELD_RING, errors.password && 'border-destructive')}
              {...register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className={cn(
                'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-md text-muted-foreground hover:text-foreground',
                CONTROL_TRANSITION,
                FIELD_RING
              )}
              aria-label={
                showPassword
                  ? t('auth.hidePassword', 'Hide password')
                  : t('auth.showPassword', 'Show password')
              }
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p id="register-password-error" className="text-xs text-destructive">
              {errors.password.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="confirmPassword">{t('auth.confirmPassword', 'Confirm password')}</Label>
          <Input
            id="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            aria-invalid={!!errors.confirmPassword}
            aria-describedby={errors.confirmPassword ? 'confirm-error' : undefined}
            className={cn(FIELD_RING, errors.confirmPassword && 'border-destructive')}
            {...register('confirmPassword')}
          />
          {errors.confirmPassword && (
            <p id="confirm-error" className="text-xs text-destructive">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {/* Consent is a decision, not a field — set apart by a hairline. */}
        <div className="flex flex-col gap-3 border-t pt-5">
          <div className="flex flex-col gap-1">
            <div className="flex items-start gap-2.5">
              <input
                id="acceptTerms"
                type="checkbox"
                className={cn('mt-0.5 h-4 w-4 shrink-0 rounded-sm accent-primary', FIELD_RING)}
                aria-invalid={!!errors.acceptTerms}
                {...register('acceptTerms')}
              />
              <Label htmlFor="acceptTerms" className="text-sm font-normal leading-snug">
                {t('auth.acceptTerms', 'I accept the')}{' '}
                <Link
                  to="/terms"
                  className={cn(
                    'rounded-sm text-primary underline-offset-4 hover:underline',
                    FIELD_RING
                  )}
                >
                  {t('common.terms', 'Terms')}
                </Link>
              </Label>
            </div>
            {errors.acceptTerms && (
              <p className="text-xs text-destructive">{errors.acceptTerms.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-start gap-2.5">
              <input
                id="acceptPrivacy"
                type="checkbox"
                className={cn('mt-0.5 h-4 w-4 shrink-0 rounded-sm accent-primary', FIELD_RING)}
                aria-invalid={!!errors.acceptPrivacy}
                {...register('acceptPrivacy')}
              />
              <Label htmlFor="acceptPrivacy" className="text-sm font-normal leading-snug">
                {t('auth.acceptPrivacy', 'I accept the')}{' '}
                <Link
                  to="/privacy"
                  className={cn(
                    'rounded-sm text-primary underline-offset-4 hover:underline',
                    FIELD_RING
                  )}
                >
                  {t('common.privacy', 'Privacy')}
                </Link>
              </Label>
            </div>
            {errors.acceptPrivacy && (
              <p className="text-xs text-destructive">{errors.acceptPrivacy.message}</p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          disabled={busy}
          className={cn(
            'h-11 w-full gap-2',
            'transition-[background-color,transform] duration-[var(--dur-micro)] ease-[var(--ease-out)]',
            'active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          {isSubmitting && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
          {t('auth.signUp', 'Sign up')}
        </Button>
      </form>

      <p className="text-sm text-muted-foreground">
        {t('auth.hasAccount', 'Already have an account?')}{' '}
        <Link
          to="/login"
          className={cn(
            'rounded-sm font-medium text-primary underline-offset-4 hover:underline',
            CONTROL_TRANSITION,
            FIELD_RING
          )}
        >
          {t('auth.signIn', 'Sign in')}
        </Link>
      </p>
    </div>
  )
}
